/**
 * OAuth for MCP clients — the SPA's side.
 *
 * Consent: `/authorize` on the API validates the request, keeps it server-side under a
 * one-time transaction id and sends the browser to `/oauth/consent?tx=<id>`. This module
 * calls the three consent endpoints with that id; the redirect URI never passes through
 * the SPA except as the server's answer (`redirectTo`).
 *
 * Connected apps: the user's OAuth grants, listed and revoked from Settings → API.
 *
 * All calls go through `apiRequest` (API_PREFIX, CapacitorHttp, token refresh).
 */

import { apiRequest, type ApiResponse } from "@/shared/utils/api/apiClient"
import { retryConsentCall } from "./consentRetry"

// ---------------------------------------------------------------------------
// Pending transaction across a login round-trip
// ---------------------------------------------------------------------------

const PENDING_TX_KEY = "totodo:oauth-consent-tx"
const TX_ID_PATTERN = /^[A-Za-z0-9_-]{22}$/
/** A transaction lives 10 minutes on the server; a remembered id is no use after that. */
const PENDING_TX_MAX_AGE_MS = 10 * 60 * 1000

export function isConsentTxId(value: string | null | undefined): value is string {
  return typeof value === "string" && TX_ID_PATTERN.test(value)
}

/** Remember ONLY the transaction id (never a redirect URI) while the user signs in. */
export function savePendingConsentTx(tx: string): void {
  try {
    sessionStorage.setItem(PENDING_TX_KEY, JSON.stringify({ tx, at: Date.now() }))
  } catch {
    /* storage unavailable — the user can start again from the app */
  }
}

export function readPendingConsentTx(): string | null {
  try {
    const raw = sessionStorage.getItem(PENDING_TX_KEY)
    if (!raw) return null
    const parsed = JSON.parse(raw) as { tx?: unknown; at?: unknown }
    const fresh = typeof parsed.at === "number" && Date.now() - parsed.at < PENDING_TX_MAX_AGE_MS
    if (fresh && typeof parsed.tx === "string" && isConsentTxId(parsed.tx)) return parsed.tx
    sessionStorage.removeItem(PENDING_TX_KEY)
    return null
  } catch {
    return null
  }
}

export function clearPendingConsentTx(): void {
  try {
    sessionStorage.removeItem(PENDING_TX_KEY)
  } catch {
    /* ignore */
  }
}

// ---------------------------------------------------------------------------
// Consent API
// ---------------------------------------------------------------------------

export type ConsentAccess = "read" | "read-write"

export interface ConsentDescription {
  client: {
    id: string
    /** Self-asserted by the app. */
    name: string
    /** True only when the name comes with a domain the app controls. */
    nameVerified: boolean
    domain?: string
  }
  redirect: { host: string; isLoopback: boolean }
  scopes: string[]
  canWrite: boolean
  hasMemory: boolean
  expiresInSeconds: number
}

export type ConsentFailure = "expired" | "error"

/**
 * `message` is for people (only when the server wrote one); `status` and `code` say what failed,
 * so a page without a message can still show something to report. `status` is absent when no
 * answer arrived at all (offline, blocked, timed out, no session token).
 */
export type ConsentResult<T> =
  | { ok: true; data: T }
  | { ok: false; reason: ConsentFailure; message?: string; status?: number; code?: string }

function failure(status: number, data: unknown): Extract<ConsentResult<never>, { ok: false }> {
  const record = data && typeof data === "object" ? (data as Record<string, unknown>) : {}
  const message = typeof record.message === "string" ? record.message : undefined
  const code =
    typeof record.code === "string" ? record.code : typeof record.error === "string" ? record.error : undefined
  return { ok: false, reason: status === 404 ? "expired" : "error", message, status, code }
}

export async function describeConsent(tx: string): Promise<ConsentResult<ConsentDescription>> {
  const res = await apiRequest<ConsentDescription>({
    path: "/oauth/consent/describe",
    method: "POST",
    body: { tx },
  })
  return res.ok ? { ok: true, data: res.data } : failure(res.status, res.data)
}

/**
 * `describeConsent`, retried (`retryConsentCall`). The consent page calls it the moment the user
 * counts as signed in, and the first call can carry a token the API refuses: seen in production
 * (2026-10-30) with a browser whose old totodo.io session still held an access token that had
 * expired months before (401), renewed only by apiRequest's own 401 path.
 */
export function describeConsentWithRetry(
  tx: string,
  isCancelled: () => boolean,
): Promise<ConsentResult<ConsentDescription>> {
  return retryConsentCall(() => describeConsent(tx), isCancelled)
}

/** Returns the URL to send the browser to: the app's own redirect URI with the code. */
export async function approveConsent(
  tx: string,
  access: ConsentAccess,
  memory: boolean,
): Promise<ConsentResult<{ redirectTo: string }>> {
  const res = await apiRequest<{ redirectTo: string }>({
    path: "/oauth/consent/approve",
    method: "POST",
    body: { tx, access, memory },
  })
  return res.ok ? { ok: true, data: res.data } : failure(res.status, res.data)
}

/** Returns the URL to send the browser to: the app's redirect URI with `error=access_denied`. */
export async function denyConsent(tx: string): Promise<ConsentResult<{ redirectTo: string }>> {
  const res = await apiRequest<{ redirectTo: string }>({
    path: "/oauth/consent/deny",
    method: "POST",
    body: { tx },
  })
  return res.ok ? { ok: true, data: res.data } : failure(res.status, res.data)
}

// ---------------------------------------------------------------------------
// Connected apps
// ---------------------------------------------------------------------------

export interface ConnectedApp {
  grantId: string
  clientId: string
  clientName: string
  scopes: string[]
  createdAt: string
}

/**
 * What loading the connected apps came to. Never thrown: the list failing to load must not read
 * as "nothing is connected", so the caller has to look at which of these it got.
 *
 * - `ok`          the list. `partial` means the server could only read part of it (older
 *                 connections may be missing).
 * - `unavailable` the API has no OAuth endpoints (404): a server that predates OAuth, or a
 *                 non-canonical API host. The caller hides the section.
 * - `error`       it could not be loaded. `httpStatus` is absent when no answer arrived at all
 *                 (offline, blocked, timed out); `reference` is the server's correlation id.
 */
export type ConnectedAppsResult =
  | { status: "ok"; apps: ConnectedApp[]; partial: boolean }
  | { status: "unavailable" }
  | { status: "error"; httpStatus?: number; message: string; reference?: string }

function isConnectedApp(value: unknown): value is ConnectedApp {
  if (!value || typeof value !== "object") return false
  const app = value as Record<string, unknown>
  return (
    typeof app.grantId === "string" &&
    typeof app.clientId === "string" &&
    typeof app.clientName === "string" &&
    typeof app.createdAt === "string" &&
    Array.isArray(app.scopes) &&
    app.scopes.every((scope) => typeof scope === "string")
  )
}

function errorFields(data: unknown): { message?: string; reference?: string } {
  if (!data || typeof data !== "object") return {}
  const record = data as Record<string, unknown>
  const message =
    typeof record.message === "string" ? record.message : typeof record.error === "string" ? record.error : undefined
  return { message, reference: typeof record.code === "string" ? record.code : undefined }
}

export async function fetchConnectedApps(): Promise<ConnectedAppsResult> {
  let res: ApiResponse<unknown>
  try {
    res = await apiRequest<unknown>({ path: "/oauth/grants", method: "GET" })
  } catch (error) {
    // No answer: fetch failed (offline, CORS-blocked, cancelled) or timed out.
    return {
      status: "error",
      message: error instanceof Error && error.message ? error.message : "Failed to load connected apps",
    }
  }
  if (res.status === 404) return { status: "unavailable" }
  if (!res.ok) {
    const { message, reference } = errorFields(res.data)
    return { status: "error", httpStatus: res.status, message: message ?? "Failed to load connected apps", reference }
  }
  // `{ grants, partial }`; an API that predates `partial` answers the bare array.
  const body = res.data
  const list = Array.isArray(body) ? body : (body as { grants?: unknown } | null)?.grants
  if (!Array.isArray(list) || !list.every(isConnectedApp)) {
    return { status: "error", httpStatus: res.status, message: "Unexpected answer from the server" }
  }
  const partial = !Array.isArray(body) && (body as { partial?: unknown }).partial === true
  return { status: "ok", apps: list, partial }
}

export async function revokeConnectedApp(grantId: string): Promise<void> {
  const res = await apiRequest<{ error?: string }>({
    path: `/oauth/grants/${encodeURIComponent(grantId)}`,
    method: "DELETE",
  })
  if (!res.ok) {
    throw new Error(res.data?.error || "Failed to disconnect app")
  }
}
